import axios from "axios";

export interface SunriseSunsetData {
  sunrise: Date;
  sunset: Date;
  solarNoon: Date;
}

export type DataSource = "tripura" | "sunrise-sunset.org" | "noaa";

// Fetch from Tripura Astronomy API (primary source)
const fetchTripuraSunriseSunset = async (
  lat: number,
  lng: number,
  date?: Date
): Promise<SunriseSunsetData> => {
  const timestamp = date ? date.toISOString() : undefined;

  const response = await axios.post(
    "https://nitya.aplicatii.yoga/api/tripura/astronomy",
    {
      latitude: lat,
      longitude: lng,
      ...(timestamp && { timestamp }),
    }
  );

  const { sunrise, sunset, noon } = response.data;

  return {
    sunrise: new Date(sunrise),
    sunset: new Date(sunset),
    solarNoon: new Date(noon),
  };
};

// Fetch from sunrise-sunset.org API
const fetchSunriseSunsetOrgData = async (
  lat: number,
  lng: number,
  date?: Date
): Promise<SunriseSunsetData> => {
  const corsProxies = [
    "https://corsproxy.io/?",
    "https://cors-anywhere.herokuapp.com/",
    "https://api.allorigins.win/raw?url=",
  ];

  let dateParam = "";
  if (date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    dateParam = `&date=${year}-${month}-${day}`;
  }

  const apiUrl = `https://api.sunrise-sunset.org/json?lat=${lat}&lng=${lng}${dateParam}&formatted=0`;

  for (const proxy of corsProxies) {
    try {
      const proxyUrl =
        proxy === "https://api.allorigins.win/raw?url="
          ? `${proxy}${encodeURIComponent(apiUrl)}`
          : `${proxy}${apiUrl}`;

      const response = await axios.get(proxyUrl);

      const {
        sunrise: sunriseTime,
        sunset: sunsetTime,
        solar_noon: solarNoonTime,
      } = response.data.results;

      return {
        sunrise: new Date(sunriseTime),
        sunset: new Date(sunsetTime),
        solarNoon: new Date(solarNoonTime),
      };
    } catch (error) {
      console.error(`Eroare cu proxy-ul ${proxy}:`, error);
    }
  }

  throw new Error("All proxies failed for sunrise-sunset.org");
};

export const fetchSunriseSunsetData = async (
  lat: number,
  lng: number,
  date?: Date,
  dataSource: DataSource = "tripura"
): Promise<SunriseSunsetData | null> => {
  // Define fallback order based on selected data source
  const fallbackOrder: DataSource[] =
    dataSource === "tripura"
      ? ["tripura", "noaa", "sunrise-sunset.org"]
      : dataSource === "noaa"
        ? ["noaa", "tripura", "sunrise-sunset.org"]
        : ["sunrise-sunset.org", "tripura", "noaa"];

  for (const source of fallbackOrder) {
    try {
      switch (source) {
        case "tripura":
          return await fetchTripuraSunriseSunset(lat, lng, date);

        case "noaa":
          return await fetchNoaaSunriseSunset(lat, lng, date || new Date());

        case "sunrise-sunset.org":
          return await fetchSunriseSunsetOrgData(lat, lng, date);
      }
    } catch (error) {
      console.error(`Eroare cu sursa ${source}:`, error);
      // Continue to the next source
    }
  }

  console.error("Toate sursele de date au eșuat");
  return null;
};

// NOAA Solar Calculator implementation
const fetchNoaaSunriseSunset = async (
  lat: number,
  lng: number,
  date: Date
): Promise<SunriseSunsetData> => {
  // Implementation based on NOAA's Solar Calculator
  // https://gml.noaa.gov/grad/solcalc/

  // Create a copy of the date to avoid modifying the original
  const targetDate = new Date(date);

  // We don't need to adjust for timezone as we're using UTC methods later

  // Calculate Julian day
  const jday = getJD(targetDate);

  // Calculate sunrise, sunset, and solar noon
  const sunriseUTC = calcSunriseUTC(jday, lat, lng);
  const sunsetUTC = calcSunsetUTC(jday, lat, lng);
  const solarNoonUTC = calcSolNoonUTC(jday, lng);

  // Convert to local time
  const sunrise = new Date(targetDate);
  sunrise.setUTCHours(0, 0, 0, 0);
  sunrise.setUTCMinutes(sunrise.getUTCMinutes() + Math.round(sunriseUTC * 60));

  const sunset = new Date(targetDate);
  sunset.setUTCHours(0, 0, 0, 0);
  sunset.setUTCMinutes(sunset.getUTCMinutes() + Math.round(sunsetUTC * 60));

  const solarNoon = new Date(targetDate);
  solarNoon.setUTCHours(0, 0, 0, 0);
  solarNoon.setUTCMinutes(solarNoon.getUTCMinutes() + Math.round(solarNoonUTC * 60));

  return {
    sunrise,
    sunset,
    solarNoon
  };
};

// Calculate the Julian Day from a Date
function getJD(date: Date): number {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  let A = Math.floor(year / 100);
  let B = 2 - A + Math.floor(A / 4);

  let JD = Math.floor(365.25 * (year + 4716)) + Math.floor(30.6001 * (month + 1)) + day + B - 1524.5;

  return JD;
}

// Convert radian angle to degrees
function radToDeg(angleRad: number): number {
  return (180.0 * angleRad / Math.PI);
}

// Convert degree angle to radians
function degToRad(angleDeg: number): number {
  return (Math.PI * angleDeg / 180.0);
}

// Calculate the Equation of Time (in minutes)
function calcEquationOfTime(t: number): number {
  const epsilon = calcObliquityCorrection(t);
  const l0 = calcGeomMeanLongSun(t);
  const e = calcEccentricityEarthOrbit(t);
  const m = calcGeomMeanAnomalySun(t);

  let y = Math.tan(degToRad(epsilon) / 2.0);
  y *= y;

  let sin2l0 = Math.sin(2.0 * degToRad(l0));
  let sinm = Math.sin(degToRad(m));
  let cos2l0 = Math.cos(2.0 * degToRad(l0));
  let sin4l0 = Math.sin(4.0 * degToRad(l0));
  let sin2m = Math.sin(2.0 * degToRad(m));

  let Etime = y * sin2l0 - 2.0 * e * sinm + 4.0 * e * y * sinm * cos2l0 - 0.5 * y * y * sin4l0 - 1.25 * e * e * sin2m;
  return radToDeg(Etime) * 4.0; // in minutes of time
}

// Calculate the hour angle of the sun at sunrise for the latitude
function calcHourAngleSunrise(lat: number, solarDec: number): number {
  const latRad = degToRad(lat);
  const sdRad = degToRad(solarDec);

  // For sunrise: altitude = -0.833 degrees
  const HAarg = (Math.sin(degToRad(-0.833)) - Math.sin(latRad) * Math.sin(sdRad)) / (Math.cos(latRad) * Math.cos(sdRad));

  // Handle special cases where the sun never rises or never sets
  if (HAarg > 1.0) {
    // Sun never rises on this location (on the specified date)
    return -1.0;
  } else if (HAarg < -1.0) {
    // Sun never sets on this location (on the specified date)
    return 1.0;
  }

  return Math.acos(HAarg);
}

// Calculate the hour angle of the sun at sunset for the latitude
function calcHourAngleSunset(lat: number, solarDec: number): number {
  // Sunset is the same as sunrise, but with the sign reversed
  const ha = calcHourAngleSunrise(lat, solarDec);
  return -ha;
}

// Calculate the solar declination
function calcSunDeclination(t: number): number {
  const e = calcObliquityCorrection(t);
  const lambda = calcSunApparentLong(t);

  const sint = Math.sin(degToRad(e)) * Math.sin(degToRad(lambda));
  return radToDeg(Math.asin(sint));
}

// Calculate the Universal Coordinated Time (UTC) of solar noon for the given day at the given location
function calcSolNoonUTC(jd: number, longitude: number): number {
  // First pass uses approximate solar noon to calculate equation of time
  const tnoon = calcTimeJulianCent(jd - longitude / 360.0);
  const eqTime = calcEquationOfTime(tnoon);
  const solNoonUTC = 720 - (longitude * 4) - eqTime; // in minutes

  const newt = calcTimeJulianCent(jd + solNoonUTC / 1440.0);
  const eqTimeNew = calcEquationOfTime(newt);
  const solNoonUTC2 = 720 - (longitude * 4) - eqTimeNew; // in minutes

  return solNoonUTC2 / 60.0; // convert to hours
}

// Calculate the Universal Coordinated Time (UTC) of sunrise for the given day at the given location
function calcSunriseUTC(jd: number, latitude: number, longitude: number): number {
  // Calculate solar declination
  const t = calcTimeJulianCent(jd);
  const eqTime = calcEquationOfTime(t);
  const solarDec = calcSunDeclination(t);

  // Calculate hour angle for sunrise
  const hourAngle = calcHourAngleSunrise(latitude, solarDec);

  // Handle special cases
  if (hourAngle === -1.0) {
    return -1.0; // Sun never rises
  } else if (hourAngle === 1.0) {
    return 1.0; // Sun never sets
  }

  // Convert hour angle to hours
  const delta = longitude + radToDeg(hourAngle);
  const timeUTC = 720 - (4.0 * delta) - eqTime; // in minutes

  return timeUTC / 60.0; // convert to hours
}

// Calculate the Universal Coordinated Time (UTC) of sunset for the given day at the given location
function calcSunsetUTC(jd: number, latitude: number, longitude: number): number {
  // Calculate solar declination
  const t = calcTimeJulianCent(jd);
  const eqTime = calcEquationOfTime(t);
  const solarDec = calcSunDeclination(t);

  // Calculate hour angle for sunset
  const hourAngle = calcHourAngleSunset(latitude, solarDec);

  // Handle special cases
  if (hourAngle === -1.0) {
    return -1.0; // Sun never rises
  } else if (hourAngle === 1.0) {
    return 1.0; // Sun never sets
  }

  // Convert hour angle to hours
  const delta = longitude + radToDeg(hourAngle);
  const timeUTC = 720 - (4.0 * delta) - eqTime; // in minutes

  return timeUTC / 60.0; // convert to hours
}

// Calculate the Julian Century from Julian Day
function calcTimeJulianCent(jd: number): number {
  return (jd - 2451545.0) / 36525.0;
}

// Calculate the Geometric Mean Longitude of the Sun
function calcGeomMeanLongSun(t: number): number {
  let L0 = 280.46646 + t * (36000.76983 + t * 0.0003032);
  while (L0 > 360.0) {
    L0 -= 360.0;
  }
  while (L0 < 0.0) {
    L0 += 360.0;
  }
  return L0; // in degrees
}

// Calculate the Geometric Mean Anomaly of the Sun
function calcGeomMeanAnomalySun(t: number): number {
  return 357.52911 + t * (35999.05029 - 0.0001537 * t); // in degrees
}

// Calculate the eccentricity of earth's orbit
function calcEccentricityEarthOrbit(t: number): number {
  return 0.016708634 - t * (0.000042037 + 0.0000001267 * t); // unitless
}

// Calculate the equation of center for the sun
function calcSunEqOfCenter(t: number): number {
  const m = calcGeomMeanAnomalySun(t);
  const mrad = degToRad(m);
  const sinm = Math.sin(mrad);
  const sin2m = Math.sin(mrad + mrad);
  const sin3m = Math.sin(mrad + mrad + mrad);

  return sinm * (1.914602 - t * (0.004817 + 0.000014 * t)) + sin2m * (0.019993 - 0.000101 * t) + sin3m * 0.000289; // in degrees
}

// Calculate the true longitude of the sun
function calcSunTrueLong(t: number): number {
  const l0 = calcGeomMeanLongSun(t);
  const c = calcSunEqOfCenter(t);

  return l0 + c; // in degrees
}

// Calculate the apparent longitude of the sun
function calcSunApparentLong(t: number): number {
  const o = calcSunTrueLong(t);

  const omega = 125.04 - 1934.136 * t;
  return o - 0.00569 - 0.00478 * Math.sin(degToRad(omega)); // in degrees
}

// Calculate the obliquity correction
function calcObliquityCorrection(t: number): number {
  const e0 = calcMeanObliquityOfEcliptic(t);
  const omega = 125.04 - 1934.136 * t;
  return e0 + 0.00256 * Math.cos(degToRad(omega)); // in degrees
}

// Calculate the mean obliquity of the ecliptic
function calcMeanObliquityOfEcliptic(t: number): number {
  const seconds = 21.448 - t * (46.8150 + t * (0.00059 - t * 0.001813));
  return 23.0 + (26.0 + seconds / 60.0) / 60.0; // in degrees
}
